import {
    acceptCompletion,
    autocompletion,
    closeBrackets,
    closeBracketsKeymap,
    completionKeymap,
    startCompletion,
} from "@codemirror/autocomplete";
import {
    addCursorAbove,
    addCursorBelow,
    copyLineDown,
    copyLineUp,
    cursorMatchingBracket,
    defaultKeymap,
    deleteLine,
    history,
    historyKeymap,
    indentLess,
    indentMore,
    indentSelection,
    insertBlankLine,
    moveLineDown,
    moveLineUp,
    redo,
    selectAll,
    selectLine,
    toggleBlockComment,
    toggleComment,
    undo,
    undoSelection,
} from "@codemirror/commands";
import { cpp } from "@codemirror/lang-cpp";
import { json } from "@codemirror/lang-json";
import { markdown } from "@codemirror/lang-markdown";
import { python } from "@codemirror/lang-python";
import { yaml, yamlLanguage } from "@codemirror/lang-yaml";
import {
    bracketMatching,
    foldAll,
    foldCode,
    foldGutter,
    foldKeymap,
    HighlightStyle,
    indentOnInput,
    indentUnit,
    LRLanguage,
    StreamLanguage,
    syntaxHighlighting,
    unfoldAll,
    unfoldCode,
    type LanguageSupport,
} from "@codemirror/language";
import { cmake } from "@codemirror/legacy-modes/mode/cmake";
import { diff } from "@codemirror/legacy-modes/mode/diff";
import { gas } from "@codemirror/legacy-modes/mode/gas";
import { properties } from "@codemirror/legacy-modes/mode/properties";
import { shell } from "@codemirror/legacy-modes/mode/shell";
import { toml } from "@codemirror/legacy-modes/mode/toml";
import { lintKeymap, nextDiagnostic, openLintPanel } from "@codemirror/lint";
import {
    findNext,
    findPrevious,
    highlightSelectionMatches,
    openSearchPanel,
    searchKeymap,
    selectNextOccurrence,
    selectSelectionMatches,
} from "@codemirror/search";
import { EditorSelection, EditorState, type Extension, type StateCommand, type Text } from "@codemirror/state";
import {
    crosshairCursor,
    drawSelection,
    dropCursor,
    EditorView,
    highlightActiveLine,
    highlightActiveLineGutter,
    highlightSpecialChars,
    keymap,
    lineNumbers,
    type KeyBinding,
    rectangularSelection,
    scrollPastEnd,
} from "@codemirror/view";
import { tags as t } from "@lezer/highlight";
import { indentationMarkers } from "@replit/codemirror-indentation-markers";

import { bracketColors } from "./brackets.ts";
import { keymapKeys, type KeyInfo } from "./commands.ts";
import { findWidget } from "./find-widget.ts";

/**
 * The editor as VS Code's defaults have it: line numbers and folding in the
 * gutter, a bordered current line, Alt+click for another cursor, Shift+Alt+drag for a column selection, and VS Code's editing keys
 * where CodeMirror's differ. Colors are CSS variables (workbench.css), so a
 * theme change needs no reconfiguration. Indentation is `indentation`'s.
 */
export function editorExtensions(): Extension[] {
    return [
        lineNumbers(),
        foldGutter({ openText: "\u{eab4}", closedText: "\u{eab6}" }),
        highlightActiveLineGutter(),
        highlightSpecialChars(),
        history(),
        drawSelection({ cursorBlinkRate: 1000 }),
        dropCursor(),
        EditorState.allowMultipleSelections.of(true),
        EditorView.clickAddsSelectionRange.of((event) => event.altKey),
        rectangularSelection({ eventFilter: (event) => event.altKey && event.shiftKey }),
        crosshairCursor({ key: "Alt" }),
        indentOnInput(),
        syntaxHighlighting(vscodeHighlight),
        bracketMatching(),
        bracketColors,
        indentationMarkers({
            thickness: 1,
            // The CSS variables pick light or dark, whichever the editor thinks it is.
            colors: {
                light: "var(--wb-indent-guide)",
                dark: "var(--wb-indent-guide)",
                activeLight: "var(--wb-indent-guide-active)",
                activeDark: "var(--wb-indent-guide-active)",
            },
        }),
        closeBrackets(),
        autocompletion(),
        highlightActiveLine(),
        highlightSelectionMatches(),
        findWidget(),
        scrollPastEnd(),
        keymap.of(editorKeymap),
    ];
}

/** The editor's keys that the keyboard shortcuts list shows, by the command they run. */
export function editorKeys(): KeyInfo[] {
    return keymapKeys(
        editorKeymap,
        [
            ["Undo", undo],
            ["Redo", redo],
            ["Soft Undo", undoSelection],
            ["Select All", selectAll],
            ["Expand Line Selection", selectLine],
            ["Add Cursor Above", addCursorAbove],
            ["Add Cursor Below", addCursorBelow],
            ["Add Selection to Next Find Match", selectNextOccurrence],
            ["Select All Occurrences of Find Match", selectSelectionMatches],
            ["Move Line Up", moveLineUp],
            ["Move Line Down", moveLineDown],
            ["Copy Line Up", copyLineUp],
            ["Copy Line Down", copyLineDown],
            ["Delete Line", deleteLine],
            ["Insert Line Below", insertBlankLine],
            ["Indent Line", indentMore],
            ["Outdent Line", indentLess],
            ["Reindent Selected Lines", indentSelection],
            ["Toggle Line Comment", toggleComment],
            ["Toggle Block Comment", toggleBlockComment],
            ["Go to Bracket", cursorMatchingBracket],
            ["Trigger Suggest", startCompletion],
            ["Find", openSearchPanel],
            ["Find Next", findNext],
            ["Find Previous", findPrevious],
            ["Fold", foldCode],
            ["Unfold", unfoldCode],
            ["Fold All", foldAll],
            ["Unfold All", unfoldAll],
            ["Go to Next Problem", nextDiagnostic],
            ["Show Problems", openLintPanel],
        ],
        "Editor",
    );
}

/** The tab width, and one indent level as a tab or as spaces to the next tab stop. */
export function indentation(tabSize: number, tabs: boolean): Extension {
    return [EditorState.tabSize.of(tabSize), indentUnit.of(tabs ? "\t" : " ".repeat(tabSize))];
}

/**
 * Whether `doc` indents with tabs, by which of the two starts more of its
 * lines; undefined when neither does. Lines indented by one space, such as
 * block comment continuations, count for neither.
 */
export function detectTabs(doc: Text): boolean | undefined {
    let tabs = 0;
    let spaces = 0;
    for (let i = 1; i <= doc.lines; i++) {
        const text = doc.line(i).text;
        if (text.startsWith("\t")) tabs++;
        else if (text.startsWith("  ")) spaces++;
    }
    return tabs === spaces ? undefined : tabs > spaces;
}

/**
 * Tab as VS Code has it: a selection over several lines is indented, and
 * otherwise each selection is replaced by a tab, or by spaces up to the next
 * tab stop, as the indent unit is.
 */
export const insertTab: StateCommand = ({ state, dispatch }) => {
    const { doc, tabSize } = state;
    const tabs = state.facet(indentUnit).startsWith("\t");
    if (state.selection.ranges.some((r) => doc.lineAt(r.from).number !== doc.lineAt(r.to).number)) {
        return indentMore({ state, dispatch });
    }
    const change = state.changeByRange((range) => {
        const line = doc.lineAt(range.from);
        let column = 0;
        for (const char of line.text.slice(0, range.from - line.from)) {
            column += char === "\t" ? tabSize - (column % tabSize) : 1;
        }
        const insert = tabs ? "\t" : " ".repeat(tabSize - (column % tabSize));
        return {
            changes: { from: range.from, to: range.to, insert },
            range: EditorSelection.cursor(range.from + insert.length),
        };
    });
    dispatch(state.update(change, { scrollIntoView: true, userEvent: "input" }));
    return true;
};

/** Declared after `insertTab`, which it binds. */
const editorKeymap: readonly KeyBinding[] = [
    { key: "Mod-Alt-ArrowUp", run: addCursorAbove },
    { key: "Mod-Alt-ArrowDown", run: addCursorBelow },
    { key: "Mod-Enter", run: insertBlankLine },
    { key: "Mod-i", run: startCompletion },
    { key: "Tab", run: acceptCompletion },
    ...closeBracketsKeymap,
    ...defaultKeymap,
    ...searchKeymap,
    ...historyKeymap,
    ...foldKeymap,
    ...completionKeymap,
    ...lintKeymap,
    { key: "Tab", run: insertTab, shift: indentLess },
];

/** VS Code's Dark Modern and Light Modern token colors, by variable. */
const vscodeHighlight = HighlightStyle.define([
    { tag: [t.comment, t.lineComment, t.blockComment], color: "var(--tok-comment)" },
    {
        tag: [t.keyword, t.definitionKeyword, t.modifier, t.operatorKeyword, t.null, t.bool, t.self],
        color: "var(--tok-keyword)",
    },
    { tag: [t.controlKeyword, t.processingInstruction, t.moduleKeyword], color: "var(--tok-control)" },
    { tag: [t.string, t.special(t.string), t.character], color: "var(--tok-string)" },
    { tag: t.escape, color: "var(--tok-escape)" },
    { tag: [t.number, t.integer, t.float], color: "var(--tok-number)" },
    { tag: [t.typeName, t.className, t.namespace], color: "var(--tok-type)" },
    { tag: t.standard(t.typeName), color: "var(--tok-keyword)" },
    {
        tag: [t.function(t.variableName), t.function(t.propertyName), t.function(t.definition(t.variableName))],
        color: "var(--tok-function)",
    },
    { tag: [t.variableName, t.propertyName, t.attributeName], color: "var(--tok-variable)" },
    { tag: [t.special(t.name), t.constant(t.name), t.macroName], color: "var(--tok-macro)" },
    { tag: t.meta, color: "var(--tok-meta)" },
    { tag: [t.heading], color: "var(--tok-keyword)", fontWeight: "bold" },
    { tag: t.emphasis, fontStyle: "italic" },
    { tag: t.strong, fontWeight: "bold" },
    { tag: t.link, color: "var(--tok-string)", textDecoration: "underline" },
    { tag: [t.monospace], color: "var(--tok-string)" },
    { tag: t.invalid, color: "var(--tok-invalid)" },
    { tag: [t.inserted], color: "var(--tok-number)" },
    { tag: [t.deleted], color: "var(--tok-string)" },
]);

/** CMake files' language, whose language data carries their completion source. */
export const cmakeLanguage = StreamLanguage.define(cmake);
const propertiesLanguage = StreamLanguage.define(properties);
const gasLanguage = StreamLanguage.define(gas);
const tomlLanguage = StreamLanguage.define(toml);
const shellLanguage = StreamLanguage.define(shell);
const diffLanguage = StreamLanguage.define(diff);
/** YAML with language data of its own, so the manifest's completion stays out of other YAML files. */
export const manifestLanguage = LRLanguage.define({
    name: "idf_component",
    parser: yamlLanguage.parser,
    languageData: { commentTokens: { line: "#" }, indentOnInput: /^\s*[\]\}]$/ },
});

/** The language of a file by its name, and the LSP language ID clangd gets. */
export function languageFor(path: string): { support?: LanguageSupport | Extension; lspId?: string } {
    const name = path.slice(path.lastIndexOf("/") + 1);
    const ext = name.includes(".") ? name.slice(name.lastIndexOf(".") + 1).toLowerCase() : "";
    if (name === "CMakeLists.txt" || ext === "cmake") return { support: cmakeLanguage };
    if (name === "idf_component.yml") return { support: manifestLanguage };
    if (/^(sdkconfig.*|.*\.defaults|.*\.ci)$/.test(name) || ext === "properties") {
        return { support: propertiesLanguage };
    }
    switch (ext) {
        case "c":
            return { support: cpp(), lspId: "c" };
        case "h":
        case "cpp":
        case "cc":
        case "cxx":
        case "hpp":
        case "hh":
        case "inc":
            return { support: cpp(), lspId: "cpp" };
        case "s":
            return { support: gasLanguage };
        case "md":
            return { support: markdown() };
        case "json":
            return { support: json() };
        case "py":
            return { support: python() };
        case "yml":
        case "yaml":
            return { support: yaml() };
        case "toml":
            return { support: tomlLanguage };
        case "sh":
            return { support: shellLanguage };
        case "diff":
        case "patch":
            return { support: diffLanguage };
    }
    return {};
}
